import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { PERMISSIONS, InviteData, LinkData, User } from '@repo/shared/domain';
import { EventEmitter, getVerifyPermission, guardedClient as request, constructQuery } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import {
	getCompany,
	getCompanyEvent,
	getCompanyEventRoles,
	getCompanyEventUsers,
	getCompanyUsers,
} from 'src/api/company';
import { useConfig } from 'src/hooks/useConfig';
import UsersTab from 'src/parts/tabs/UsersTab';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/events/$eventId/users')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		page: (search.page as number) || 0,
		sort: (search.sort as string) || '',
		filter: (search.filter as string) || '',
	}),
});

function RouteComponent() {
	const { groupId, eventId } = Route.useParams();
	const { page, sort, filter } = Route.useSearch();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const query = constructQuery({ page, sort, filter });

	const { data: group } = useQuery({
		queryKey: ['company-group', groupId],
		queryFn: () => getCompany(groupId),
		enabled: !userLoading,
	});

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['company-event', groupId, eventId],
		queryFn: () => getCompanyEvent(groupId, eventId),
		enabled: !userLoading,
	});

	const { data: roles } = useQuery({
		queryKey: ['company-event-roles', groupId, eventId],
		queryFn: () => getCompanyEventRoles(groupId, eventId),
		enabled: !userLoading,
	});

	const { data: users, isLoading: usersLoading } = useQuery({
		queryKey: ['company-event-users', groupId, eventId, { page, sort, filter }],
		queryFn: () => getCompanyEventUsers(groupId, eventId, query),
		enabled: !userLoading,
	});

	const { data: groupUsers, isLoading: groupUsersLoading } = useQuery({
		queryKey: ['company-users', groupId],
		queryFn: () => getCompanyUsers(groupId),
		enabled: !userLoading,
	});

	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const defaultCountry = config?.defaultCountry || 'BE';

	useBreadcrumbLevel(3, 'Users');

	if (userLoading || eventLoading || usersLoading || groupUsersLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !event) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);
	const isOwnGroupEvent = event.groupIds?.includes(groupId) ?? false;
	const requirePhoneOrEmail = featureFlags?.IS_ALHEEMBOUW || false;

	const revalidateUsers = () => {
		queryClient.invalidateQueries({ queryKey: ['company-event-users', groupId, eventId] });
		queryClient.invalidateQueries({ queryKey: ['company-users', groupId] });
	};

	const handleLinkUser = async (linkData: LinkData) => {
		const { userId, roleIds } = linkData;
		if (!userId || event.userIds?.includes(userId)) return;
		try {
			await request.post(`/api/company-portal/groups/${groupId}/events/${eventId}/users`, { userId, roleIds });
			EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
		} catch (error) {
			console.error(error);
			EventEmitter.emit('toast', { type: 'error', message: 'Error linking user to event' });
		}
		revalidateUsers();
	};

	const handleInviteUser = async (data: InviteData) => {
		try {
			const { body: user } = await request.post<User>(`/api/company-portal/groups/${groupId}/users`, data);
			await handleLinkUser({ userId: user.id });
			return user;
		} catch (error: any) {
			EventEmitter.emit('toast', { type: 'error', message: error.message || 'Error adding user to event' });
			throw error;
		}
	};

	const handleDeleteUser = async (userId: string) => {
		try {
			await request.del(`/api/company-portal/groups/${groupId}/events/${eventId}/users/${userId}`, {});
			EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Error' });
		}
		revalidateUsers();
	};

	return (
		<UsersTab
			onInvite={handleInviteUser}
			onDelete={handleDeleteUser}
			onReload={revalidateUsers}
			onLink={handleLinkUser}
			event={event}
			roles={roles || []}
			authenticatedUser={authenticatedUser}
			users={users}
			readOnly
			filter={filter}
			permissions={{
				archive: isOwnGroupEvent && verifyPermission(PERMISSIONS.companyEventsEdit),
				update: isOwnGroupEvent && verifyPermission(PERMISSIONS.companyEventsEdit),
				create:
					isOwnGroupEvent &&
					verifyPermission(PERMISSIONS.companyEventsEdit) &&
					verifyPermission(PERMISSIONS.companyUsersCreate) &&
					!(group?.legalForm === 'selfEmployed' && groupUsers?.count && groupUsers.count >= 2),
				canPrintBadges: false,
				canCreateWithoutEmail: false,
				isUserAdmin: false,
				canImport: group?.legalForm !== 'selfEmployed',
			}}
			addUserButtonState={{
				disabled: event.source === 'calendar',
				tooltip: 'This event does not allow adding users through this interface.',
			}}
			defaultCountry={defaultCountry || 'BE'}
			requirePhoneOrEmail={requirePhoneOrEmail}
			importEndpoint={`/api/company-portal/groups/${groupId}/events/${eventId}/users/import`}
		/>
	);
}
